import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../components/ui/chart';
import { AXIS } from '../components/reports/chartTheme';
import { tipFormatter, AreaGradient } from '../components/reports/ChartKit';
import {
  ComposedChart, AreaChart, Area, Bar, Line, XAxis, YAxis, CartesianGrid
} from 'recharts';

/**
 * Finanzas → Posición: ¿cuánta plata tengo, cuánta me deben, cuánta debo,
 * y cómo viene la película las próximas semanas?
 * Foto de hoy + saldos por cuenta + calendario de cheques + aging + evolución.
 * Todo "según lo cargado": los bloques sin datos avisan qué falta cargar.
 */

const money = (n: number) => {
  if (n == null) return '—';
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3)}k`;
  return `${sign}$${Math.round(a).toLocaleString('es-AR')}`;
};

const AGING_SEGS: Array<[string, string, string]> = [
  ['b0_30', '0-30 días', 'hsl(var(--chart-2))'],
  ['b31_60', '31-60', '#378ADD'],
  ['b61_90', '61-90', '#EF9F27'],
  ['b90', '+90', '#E24B4A']
];

export default function FinancialPosition() {
  const navigate = useNavigate();
  const [p, setP] = useState<any>(null);
  const [cobrar, setCobrar] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/reports/tablero/posicion')
      .then((r) => setP(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando la posición financiera'));
    axios.get('/api/reports/tablero/cobrar').then((r) => setCobrar(r.data)).catch(() => {});
  }, []);

  // Click en una tarjeta "(sin asignar)": ir a la página de asignación filtrada por ese bucket
  const openAsignar = (cuentaLabel: string) => {
    const bucket = cuentaLabel.replace(' (sin asignar)', '');
    navigate(`/finanzas/asignar-cuentas?bucket=${encodeURIComponent(bucket)}`);
  };

  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!p) return <div className="py-10 text-center text-muted-foreground">Cargando posición...</div>;

  const fecha = (s: string) => new Date(s + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
  const agingTotal = AGING_SEGS.reduce((a, [k]) => a + (p.agingClientes?.[k] || 0), 0) || 1;
  const hayCalendario = p.calendario?.some((s: number | any) => s.entra > 0 || s.sale > 0);
  const deudores = (cobrar?.clientes || []).slice(0, 8);

  return (
    <div className="space-y-4 p-4 sm:p-6 max-w-7xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Posición financiera</h1>
        <p className="text-sm text-muted-foreground">Foto de hoy según lo cargado en el sistema — no depende de ningún período.</p>
      </div>

      {/* Foto de hoy */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Disponible (según lo cargado)</p>
          <p className={`text-2xl font-semibold leading-none mb-1.5 ${p.disponible < 0 ? 'text-red-600' : ''}`}>{money(p.disponible)}</p>
          <span className="text-xs text-muted-foreground">suma de las cuentas de abajo</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Por entrar</p>
          <p className="text-2xl font-semibold leading-none mb-1.5 text-emerald-600">{money(p.porEntrar)}</p>
          <span className="text-xs text-muted-foreground">cta. cte. {money(p.clientes.saldo)} · cheques {money(p.chequesRecibidos.monto)}</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Por salir</p>
          <p className="text-2xl font-semibold leading-none mb-1.5 text-red-600">{money(p.porSalir)}</p>
          <span className="text-xs text-muted-foreground">proveedores {money(p.proveedores.saldo)} · cheques dados {money(p.chequesEmitidos.monto)}</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Posición neta comprometida</p>
          <p className={`text-2xl font-semibold leading-none mb-1.5 ${p.neta < 0 ? 'text-red-600' : ''}`}>{money(p.neta)}</p>
          <span className="text-xs text-muted-foreground">por entrar − por salir</span>
        </CardContent></Card>
      </div>

      {/* Saldos por cuenta */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-sm">Saldos por cuenta</h3>
            <span className="text-xs text-muted-foreground">cobros − gastos con medio asignado · desde que se usa el sistema</span>
          </div>
          {p.cuentas.length === 0 ? (
            <p className="text-sm text-muted-foreground">Todavía no hay movimientos con medio de pago cargado.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
              {p.cuentas.map((c: any) => {
                const esSinAsignar = c.cuenta.includes('sin asignar') || c.cuenta === 'Sin asignar';
                return (
                  <div
                    key={c.cuenta}
                    className={`rounded-lg border p-3 ${esSinAsignar ? 'border-dashed cursor-pointer hover:bg-muted/40 transition-colors' : ''}`}
                    onClick={esSinAsignar ? () => openAsignar(c.cuenta) : undefined}
                  >
                    <p className="text-xs text-muted-foreground">{c.cuenta}</p>
                    <p className={`text-xl font-semibold ${c.saldo < 0 ? 'text-red-600' : ''}`}>{money(c.saldo)}</p>
                    {esSinAsignar && <p className="text-[11px] text-muted-foreground underline underline-offset-2">repartir a cuentas →</p>}
                  </div>
                );
              })}
              <div className="rounded-lg border p-3 border-dashed">
                <p className="text-xs text-muted-foreground">Efectivo último cierre</p>
                <p className="text-xl font-semibold">{p.efectivoUltimoCierre ? money(p.efectivoUltimoCierre.monto) : '—'}</p>
                <p className="text-[11px] text-muted-foreground">{p.efectivoUltimoCierre ? `contado el ${fecha(p.efectivoUltimoCierre.fecha)}` : 'sin cierres'}</p>
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Los gastos operativos ahora piden medio de pago; los que se cargaron sin medio aparecen en "Sin asignar".
          </p>
        </CardContent>
      </Card>

      {/* Calendario de plata */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-sm">Calendario de plata · próximas 12 semanas</h3>
            <div className="flex gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm" style={{ background: 'hsl(var(--chart-2))' }} />Entra (cheques)</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm" style={{ background: '#E24B4A' }} />Sale (cheques)</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 rounded" style={{ background: 'hsl(var(--chart-1))' }} />Saldo proyectado</span>
            </div>
          </div>
          {!hayCalendario ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Sin cheques con vencimiento cargados. Cuando se carguen los cheques dados y tomados en
              <span className="font-medium"> Finanzas → Cheques</span>, acá vas a ver semana a semana cuánta plata entra, cuánta sale y con cuánto quedás.
            </p>
          ) : (
            <ChartContainer
              config={{
                entra: { label: 'Entra', color: 'hsl(var(--chart-2))' },
                sale: { label: 'Sale', color: '#E24B4A' },
                saldoProyectado: { label: 'Saldo proyectado', color: 'hsl(var(--chart-1))' }
              }}
              className="h-[260px] w-full"
            >
              <ComposedChart data={p.calendario} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                <XAxis dataKey="label" {...AXIS} />
                <YAxis {...AXIS} tickFormatter={(v: number) => money(v)} width={56} />
                <ChartTooltip content={<ChartTooltipContent formatter={tipFormatter({ entra: 'Entra', sale: 'Sale', saldoProyectado: 'Saldo proyectado' }, money)} />} />
                <Bar dataKey="entra" fill="var(--color-entra)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="sale" fill="var(--color-sale)" radius={[3, 3, 0, 0]} />
                <Line type="monotone" dataKey="saldoProyectado" stroke="var(--color-saldoProyectado)" strokeWidth={2} dot={{ r: 2.5 }} />
              </ComposedChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Aging espejado */}
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Te deben vs. debés · antigüedad</h3>
            <p className="text-xs text-muted-foreground mb-1.5">La calle te debe {money(p.clientes.saldo)} ({p.clientes.cuentas} cuentas)</p>
            <div className="flex h-4 w-full rounded overflow-hidden mb-1.5">
              {AGING_SEGS.map(([k, , color]) => (
                (p.agingClientes?.[k] || 0) > 0
                  ? <div key={k} style={{ flex: p.agingClientes[k] / agingTotal, background: color }} />
                  : null
              ))}
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-4">
              {AGING_SEGS.map(([k, label, color]) => (
                <span key={k} className="flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
                  {label}: {money(p.agingClientes?.[k] || 0)}
                </span>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mb-1.5">Vos debés {money(p.proveedores.saldo)}</p>
            {p.proveedores.saldo > 0 ? (
              <div className="flex h-4 w-full rounded overflow-hidden bg-red-500/60" />
            ) : (
              <p className="text-xs text-muted-foreground">Sin deudas a proveedores cargadas en cta. cte. — cuando se carguen, acá aparece el espejo.</p>
            )}
          </CardContent>
        </Card>

        {/* Evolución de la posición: entrar / salir / neta */}
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
              <h3 className="font-semibold text-sm">Evolución de la posición</h3>
              <div className="flex gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 rounded" style={{ background: 'hsl(var(--chart-2))' }} />Por entrar</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 rounded" style={{ background: '#E24B4A' }} />Por salir</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 rounded" style={{ background: 'hsl(var(--chart-1))' }} />Neta</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Últimos 12 meses · cta. cte. reconstruida + cheques vigentes en cada mes</p>
            {(p.evolucionPosicion || []).length < 2 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Todavía no hay historial suficiente.</p>
            ) : (
              <ChartContainer
                config={{
                  entrar: { label: 'Por entrar', color: 'hsl(var(--chart-2))' },
                  salir: { label: 'Por salir', color: '#E24B4A' },
                  neta: { label: 'Neta', color: 'hsl(var(--chart-1))' }
                }}
                className="h-[200px] w-full"
              >
                <ComposedChart data={p.evolucionPosicion} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                  <defs>
                    <AreaGradient id="fillNeta" color="var(--color-neta)" />
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                  <XAxis dataKey="label" {...AXIS} />
                  <YAxis {...AXIS} tickFormatter={(v: number) => money(v)} width={56} />
                  <ChartTooltip content={<ChartTooltipContent formatter={tipFormatter({ entrar: 'Por entrar', salir: 'Por salir', neta: 'Neta' }, money)} />} />
                  <Area type="monotone" dataKey="neta" stroke="var(--color-neta)" strokeWidth={2.2} fill="url(#fillNeta)" dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="entrar" stroke="var(--color-entrar)" strokeWidth={1.8} dot={false} />
                  <Line type="monotone" dataKey="salir" stroke="var(--color-salir)" strokeWidth={1.8} strokeDasharray="5 4" dot={false} />
                </ComposedChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Vencimientos de cheques */}
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-semibold text-sm">Próximos vencimientos de cheques</h3>
              <a href="/cash-register/checks" className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground">ir a Cheques</a>
            </div>
            {p.vencimientos.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin cheques pendientes cargados.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {p.vencimientos.map((v: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="py-1 pr-2 whitespace-nowrap">{fecha(v.vence)}</td>
                      <td className={`py-1 pr-2 ${v.tipo === 'received' ? 'text-emerald-600' : 'text-red-600'}`}>{v.tipo === 'received' ? 'Entra' : 'Sale'}</td>
                      <td className="py-1 pr-2 truncate max-w-[200px]">{v.contraparte}{v.banco ? ` · ${v.banco}` : ''}</td>
                      <td className="py-1 text-right font-medium whitespace-nowrap">{money(v.monto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Top deudores */}
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Principales deudores</h3>
            {deudores.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin deudas pendientes. 🎉</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {deudores.map((d: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="py-1 pr-2 truncate max-w-[200px]">{d.name}</td>
                      <td className="py-1 pr-2 text-right font-medium whitespace-nowrap">{money(d.balance)}</td>
                      <td className={`py-1 text-right whitespace-nowrap text-xs ${d.oldestDays > 60 ? 'text-red-600' : d.oldestDays > 30 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                        {d.oldestDays > 0 ? `${d.oldestDays} días` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {(!p.hayCheques || !p.hayProveedores) && (
        <p className="text-xs text-amber-600 px-1">
          ⚠ La posición está incompleta:
          {!p.hayCheques && ' faltan cargar los cheques dados y tomados (Finanzas → Cheques)'}
          {!p.hayCheques && !p.hayProveedores && ' ·'}
          {!p.hayProveedores && ' las deudas a proveedores no se cargan en cta. cte.'}
          . A medida que se carguen, todos los bloques se completan solos.
        </p>
      )}
    </div>
  );
}
